import { Stack, useTheme } from '@mui/material';
import { useState } from 'react';

import { ATSAutomationListTestIds } from 'data-testids/ATS';
import { useAutomation } from 'features/Automations/context/AutomationContext';
import { AutomationListCTAProps } from 'features/Automations/types';
import { ACTION_TYPES } from 'features/Automations/reducers/automationReducer';
import { PrimaryButton, SecondaryButton } from 'sharedComponents/Buttons';
import {
  primaryButtonOverrideSx,
  secondaryButtonOverrideSx,
} from 'sharedComponents/Buttons/styles';
import { ConfirmationDialog } from 'sharedComponents/Dialog/ConfirmationDialog';
import { useQueryGetAutomationsListData } from 'features/Automations/api/listQueries';
import { useAutomationMutations } from 'features/Automations/hooks/useAutomationMutations';

export const AutomationListCTA: React.FC<AutomationListCTAProps> = ({
  automationState,
  automationDispatch,
}) => {
  const theme = useTheme();
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const { onClose, roleId } = useAutomation();
  const { data: automations = [] } = useQueryGetAutomationsListData(roleId);
  const saveMutation = useAutomationMutations(roleId);

  const handleSaveAutomations = async () => {
    try {
      await saveMutation.mutateAsync(automationState.automations);
      automationDispatch({
        type: ACTION_TYPES.SET_AUTOMATION_LIST,
        payload: {
          automations,
          hasUnsavedChanges: false,
        },
      });
    } catch (error) {
      console.error('Failed to save automations:', error);
    }
  };

  const handleDiscardClick = () => {
    if (automationState.hasUnsavedChanges) {
      setShowDiscardConfirmation(true);
    } else {
      onClose();
    }
  };

  const handleDiscardConfirm = (confirmed: boolean) => {
    setShowDiscardConfirmation(false);
    if (confirmed) {
      automationDispatch({
        type: ACTION_TYPES.SET_AUTOMATION_LIST,
        payload: {
          automations,
          hasUnsavedChanges: false,
        },
      });
      onClose();
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" gap={2}>
        <SecondaryButton
          dataTestId={ATSAutomationListTestIds.CLOSE_BUTTON}
          onClick={handleDiscardClick}
          sx={{ ...secondaryButtonOverrideSx(theme) }}
        >
          {automationState.hasUnsavedChanges ? 'Discard Changes' : 'Close'}
        </SecondaryButton>
        <PrimaryButton
          sx={{ ...primaryButtonOverrideSx(theme) }}
          disabled={!automationState.hasUnsavedChanges}
          onClick={handleSaveAutomations}
        >
          Save
        </PrimaryButton>
      </Stack>

      <ConfirmationDialog
        isOpen={showDiscardConfirmation}
        title="Discard Changes"
        text="Are you sure you want to discard all unsaved changes? This action cannot be undone."
        backLabel="Cancel"
        confirmLabel="Discard"
        resultHandler={handleDiscardConfirm}
      />
    </>
  );
};
